import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, X } from "lucide-react";
import axios from "axios";
import SiteFooter from "../components/SiteFooter";
import { LogoWordmark } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRADE_TYPES = [
  "Plumber", "Electrician", "Builder", "Carpenter", "Painter & Decorator",
  "Roofer", "Gas Engineer", "Landscaper", "HVAC Technician", "Locksmith",
  "Joiner", "Handyman", "Kitchen fitter", "Bathroom fitter", "Groundworker", "Other",
];

const STAGES = [
  { v: "new", label: "Just starting out" },
  { v: "1-2y", label: "1–2 years trading" },
  { v: "3-5y", label: "3–5 years trading" },
  { v: "5y+", label: "5+ years, well established" },
];

const CONTACTS = [
  { v: "whatsapp", label: "WhatsApp" },
  { v: "phone", label: "Phone call" },
  { v: "email", label: "Email" },
];

const PACKAGE_LABELS = {
  start: "ReadyTrades Start (£49/mo)",
  pro: "ReadyTrades Pro (£69/mo)",
  growth: "ReadyTrades Growth (£99/mo)",
};

const initialForm = {
  name: "", business_name: "", trade_type: "", location: "",
  phone: "", email: "", current_website: "",
  business_stage: "", preferred_contact: "whatsapp",
  description: "", package_interest_id: "",
};

const PACKAGE_OPTIONS = [
  { v: "", label: "No preference" },
  { v: "start", label: "Start · £49" },
  { v: "pro", label: "Pro · £69" },
  { v: "growth", label: "Growth · £99" },
];

// Memoised picker: hides the "No preference" option once a package is preselected,
// so the calculation runs only when its inputs change (not on every keystroke).
const PackageOptions = ({ hasPreselection, current, onSelect }) => {
  // PACKAGE_OPTIONS is a stable module-level constant — safe to omit from deps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const opts = useMemo(
    () => (hasPreselection ? PACKAGE_OPTIONS.slice(1) : PACKAGE_OPTIONS),
    [hasPreselection],
  );
  return opts.map((opt) => (
    <button
      type="button"
      key={opt.v || "none"}
      onClick={() => onSelect(opt.v)}
      className={`px-3 py-2.5 rounded-lg text-[13px] font-semibold border transition-colors ${
        current === opt.v
          ? "bg-[color:var(--ink)] text-white border-[color:var(--ink)]"
          : "bg-white border-[color:var(--border)] text-[color:var(--ink)] hover:border-[color:var(--ink)]"
      }`}
      data-testid={`pkg-opt-${opt.v || "none"}`}
    >
      {opt.label}
    </button>
  ));
};

const Field = ({ label, required, children, hint }) => (
  <div>
    <label className="block text-[13px] font-semibold text-[color:var(--ink)] mb-1.5">
      {label} {required && <span className="text-[color:var(--accent)]">*</span>}
      {hint && <span className="ml-1.5 text-[color:var(--muted)] font-normal">— {hint}</span>}
    </label>
    {children}
  </div>
);

const REQUIRED_FIELDS = ["name", "business_name", "trade_type", "phone", "email"];

// Turn the internal form state into the payload the API expects.
// Labels (not raw values) are stored server-side because they're what admins read.
const buildLeadPayload = (formData) => ({
  name: formData.name,
  business_name: formData.business_name,
  trade_type: formData.trade_type,
  phone: formData.phone,
  email: formData.email,
  current_website: formData.current_website,
  location: formData.location,
  business_stage: STAGES.find((s) => s.v === formData.business_stage)?.label || "",
  preferred_contact: CONTACTS.find((c) => c.v === formData.preferred_contact)?.label || "",
  description: formData.description,
  package_interest: PACKAGE_LABELS[formData.package_interest_id] || "",
});

const buildErrorMessage = (error) => {
  const detail = error.response?.data?.detail;
  if (Array.isArray(detail)) return detail[0]?.msg || "Please check your details and try again.";
  if (typeof detail === "string") return detail;
  return "Something went wrong. Please try again.";
};

const isFormComplete = (formData) => REQUIRED_FIELDS.every((f) => formData[f]?.trim());

const GetStartedPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPkg = searchParams.get("package") || "";
  const [formData, setFormData] = useState({ ...initialForm, package_interest_id: initialPkg });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const update = (field, value) => setFormData((p) => ({ ...p, [field]: value }));

  const clearPackage = () => {
    update("package_interest_id", "");
    const params = new URLSearchParams(searchParams);
    params.delete("package");
    setSearchParams(params, { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Guard: ignore clicks while a request is in flight (belt & braces alongside disabled attr).
    if (submitting) return;
    if (!isFormComplete(formData)) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/leads`, buildLeadPayload(formData));
      // The lead was saved — but we surface honest state about the email chain so the customer
      // still gets a clear message even if the confirmation email is delayed by the ESP.
      const data = res?.data || {};
      navigate("/thank-you", {
        state: {
          lead: { name: formData.name, business_name: formData.business_name },
          confirmation_email_sent: data.user_email_sent !== false,
        },
        replace: true,
      });
    } catch (error) {
      toast.error(buildErrorMessage(error));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--bg)] flex flex-col" data-testid="get-started-page">
      <header className="border-b border-[color:var(--border-soft)] bg-[color:var(--bg)]">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="logo-link"><LogoWordmark size={30} /></Link>
          <Link to="/" className="btn-ghost text-[14px]">
            <ArrowLeft size={14} /> Back to home
          </Link>
        </div>
      </header>

      <main className="flex-1 py-16 lg:py-24 px-6 lg:px-10">
        <div className="max-w-[1140px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
          {/* Left: pitch */}
          <aside className="lg:col-span-4">
            <p className="eyebrow eyebrow-accent">Get your free preview</p>
            <h1 className="font-display text-[38px] lg:text-[48px] leading-[1.02] tracking-tight text-[color:var(--ink)] mt-6" data-testid="page-title">
              Tell us about your trade — <span className="font-serif-italic">we'll do the rest</span>.
            </h1>
            <p className="text-[15px] leading-relaxed text-[color:var(--muted)] mt-5">
              A short form, takes under two minutes. Elliot will personally call or WhatsApp you within 48 hours to talk through your goals and start designing your free website preview.
            </p>

            <div className="mt-8 space-y-4">
              {[
                "No card, no contract — just a chat",
                "See your full website before you pay",
                "Live within 7 working days once you approve",
              ].map((line) => (
                <div key={line} className="flex items-start gap-3 text-[14px] text-[color:var(--ink)]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--accent)] mt-2 flex-shrink-0" />
                  <span>{line}</span>
                </div>
              ))}
            </div>

            <div className="mt-10 hidden lg:block">
              <p className="text-[12px] uppercase tracking-widest text-[color:var(--muted)] font-semibold">Prefer to email?</p>
              <a href="mailto:contact@readytrades.co.uk" className="mt-1 inline-block font-display text-[20px] text-[color:var(--ink)] hover:text-[color:var(--accent)] transition-colors">
                contact@readytrades.co.uk
              </a>
            </div>
          </aside>

          {/* Right: form */}
          <div className="lg:col-span-8">
            <form
              onSubmit={handleSubmit}
              className="bg-[color:var(--bg-paper)] border border-[color:var(--border-soft)] rounded-2xl p-6 lg:p-10"
              data-testid="lead-form"
            >
              {formData.package_interest_id && PACKAGE_LABELS[formData.package_interest_id] && (
                <div className="flex items-center gap-3 mb-8 bg-[color:var(--accent-soft)] border border-[color:var(--accent)]/30 rounded-lg px-4 py-3" data-testid="package-banner">
                  <div className="flex-1">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-[color:var(--accent-ink)]">Enquiring about</p>
                    <p className="text-[15px] font-semibold text-[color:var(--ink)]">{PACKAGE_LABELS[formData.package_interest_id]}</p>
                  </div>
                  <button type="button" onClick={clearPackage} className="p-1 text-[color:var(--muted)] hover:text-[color:var(--ink)]" data-testid="clear-package-btn">
                    <X size={16} />
                  </button>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Your name" required>
                  <input className="form-input" value={formData.name} onChange={(e) => update("name", e.target.value)} placeholder="John Smith" data-testid="lead-name-input" />
                </Field>
                <Field label="Business name" required>
                  <input className="form-input" value={formData.business_name} onChange={(e) => update("business_name", e.target.value)} placeholder="Smith Plumbing & Heating" data-testid="lead-business-input" />
                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
                <Field label="Trade" required>
                  <select className="form-input" value={formData.trade_type} onChange={(e) => update("trade_type", e.target.value)} data-testid="lead-trade-select">
                    <option value="">Select your trade</option>
                    {TRADE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Town or area you work in" hint="e.g. Sheffield · Essex">
                  <input className="form-input" value={formData.location} onChange={(e) => update("location", e.target.value)} placeholder="Great Dunmow, Essex" data-testid="lead-location-input" />
                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
                <Field label="Phone number" required>
                  <input type="tel" className="form-input" value={formData.phone} onChange={(e) => update("phone", e.target.value)} placeholder="07123 456789" data-testid="lead-phone-input" />
                </Field>
                <Field label="Email address" required>
                  <input type="email" className="form-input" value={formData.email} onChange={(e) => update("email", e.target.value)} placeholder="john@example.com" data-testid="lead-email-input" />
                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
                <Field label="Current website" hint="Optional">
                  <input className="form-input" value={formData.current_website} onChange={(e) => update("current_website", e.target.value)} placeholder="https://…" data-testid="lead-website-input" />
                </Field>
                <Field label="Business stage" hint="Optional">
                  <select className="form-input" value={formData.business_stage} onChange={(e) => update("business_stage", e.target.value)} data-testid="lead-stage-select">
                    <option value="">Select…</option>
                    {STAGES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                  </select>
                </Field>
              </div>

              <div className="mt-5">
                <Field label="Preferred package" hint="Optional — you can change this anytime">
                  <div className="grid grid-cols-3 gap-2" data-testid="package-picker">
                    <PackageOptions
                      hasPreselection={Boolean(formData.package_interest_id)}
                      current={formData.package_interest_id}
                      onSelect={(v) => update("package_interest_id", v)}
                    />
                  </div>
                </Field>
              </div>

              <div className="mt-5">
                <Field label="Anything you'd like us to know?" hint="Optional">
                  <textarea
                    rows={4}
                    className="form-input resize-none"
                    value={formData.description}
                    onChange={(e) => update("description", e.target.value)}
                    placeholder="What kind of business do you want to build? Anything you'd like us to focus on?"
                    data-testid="lead-desc-input"
                  />
                </Field>
              </div>

              <div className="mt-5">
                <Field label="Best way to reach you">
                  <div className="flex flex-wrap gap-2">
                    {CONTACTS.map((c) => (
                      <button
                        type="button"
                        key={c.v}
                        onClick={() => update("preferred_contact", c.v)}
                        className={`px-4 py-2 rounded-full text-[13px] font-semibold border transition-colors ${
                          formData.preferred_contact === c.v
                            ? "bg-[color:var(--ink)] text-white border-[color:var(--ink)]"
                            : "bg-white border-[color:var(--border)] text-[color:var(--ink)] hover:border-[color:var(--ink)]"
                        }`}
                        data-testid={`contact-opt-${c.v}`}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                </Field>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="btn-primary w-full mt-8 text-[15px] py-4"
                data-testid="lead-submit-btn"
              >
                {submitting ? (
                  <><Loader2 className="animate-spin" size={16} /> Sending…</>
                ) : (
                  <>Get my free website preview <ArrowRight size={16} /></>
                )}
              </button>

              <p className="text-[12px] text-[color:var(--muted)] mt-4 text-center">
                By submitting you agree to be contacted by ReadyTrades. We never share your details with third parties. Read our <Link to="/privacy" className="underline">privacy policy</Link>.
              </p>
            </form>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default GetStartedPage;
